import type { BatchPredicate, Command, Effect, EaseSpec, MotionProp, PlaybackConfig } from '../store/types';

let agentMsgSeq = 0;
const newAgentMsgId = () => `cmsg_a_${Date.now()}_${agentMsgSeq++}`;

export function toolUseToCommand(name: string, input: Record<string, unknown>): Command | null {
  const s = (k: string) => (typeof input[k] === 'string' ? (input[k] as string) : '');
  const n = (k: string) => (typeof input[k] === 'number' ? (input[k] as number) : 0);
  const arr = (k: string) => (Array.isArray(input[k]) ? (input[k] as string[]) : []);
  const b = (k: string) => (typeof input[k] === 'boolean' ? (input[k] as boolean) : false);
  const obj = (k: string): Record<string, string> => {
    const v = input[k];
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out: Record<string, string> = {};
    for (const [key, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === 'string') out[key] = val;
    }
    return out;
  };

  switch (name) {
    // v1 carryover
    case 'create_frame':
      return { t: 'frame.create', id: s('id'), x: n('x'), y: n('y'), w: n('w'), title: s('title'), html: s('html') };
    case 'update_frame_html':
      return { t: 'frame.updateHtml', id: s('id'), html: s('html') };

    // cf-id-scoped node mutators (Phase 0.1) — Commands+reducers already exist.
    case 'edit_node':
      return { t: 'frame.editNode', frameId: s('frameId'), cfNodeId: s('cfNodeId'), text: s('text') };
    case 'set_node_media':
      return { t: 'frame.setNodeMedia', frameId: s('frameId'), cfNodeId: s('cfNodeId'), ref: s('ref') };
    case 'delete_node':
      return { t: 'frame.deleteNode', frameId: s('frameId'), cfNodeId: s('cfNodeId') };
    case 'duplicate_node':
      return { t: 'frame.duplicateNode', frameId: s('frameId'), cfNodeId: s('cfNodeId') };
    case 'set_node_html':
      return { t: 'frame.setNodeHtml', frameId: s('frameId'), cfNodeId: s('cfNodeId'), html: s('html') };
    case 'insert_node': {
      const p = s('position');
      const position = p === 'before' || p === 'after' ? p : 'inside';
      return { t: 'frame.insertNode', frameId: s('frameId'), refCfNodeId: s('refCfNodeId'), html: s('html'), position };
    }

    case 'set_effect':
      return { t: 'frame.setEffect', id: s('id'), effect: effectFromInput(input) };
    case 'move_frames':
      return { t: 'frame.move', ids: arr('ids'), dx: n('dx'), dy: n('dy') };
    case 'resize_frame':
      return { t: 'frame.resize', id: s('id'), w: n('w'), h: n('h') };
    // NOTE: no delete_frames / clear-canvas tool is exposed to the agent — it
    // must never destroy the user's existing work. New ideas go in empty space
    // alongside what's there; the user deletes frames manually. See persona.
    case 'select_frames':
      return { t: 'select', ids: arr('ids') };
    case 'focus_view':
      return { t: 'viewport.focus', bounds: { x: n('x'), y: n('y'), w: n('w'), h: n('h') } };

    // Living Components — Main / Instance lifecycle
    case 'promote_to_main':
      return { t: 'frame.promoteToMain', id: s('id'), locked: b('locked') };
    case 'demote_from_main':
      return { t: 'frame.demoteFromMain', id: s('id') };
    case 'set_main_locked':
      return { t: 'main.setLocked', id: s('id'), locked: b('locked') };
    case 'create_instance': {
      const cmd: Command = {
        t: 'instance.create',
        id: s('id'),
        mainId: s('mainId'),
        x: n('x'),
        y: n('y'),
        slotValues: obj('slotValues'),
      };
      if (typeof input.w === 'number') (cmd as { w?: number }).w = input.w as number;
      return cmd;
    }
    case 'set_instance_slot':
      return { t: 'instance.setSlot', id: s('id'), slotName: s('slotName'), value: s('value') };
    case 'set_instance_slots':
      return { t: 'instance.setSlots', id: s('id'), slotValues: obj('slotValues') };
    case 'detach_instance':
      return { t: 'instance.detach', id: s('id') };

    // Sub-element extract
    case 'extract_subtree':
      return {
        t: 'main.extractSubtree',
        mainId: s('mainId'),
        cfNodeId: s('cfNodeId'),
        newMainId: s('newMainId'),
        x: n('x'),
        y: n('y'),
        title: s('title'),
      };

    // Batch / cross-canvas
    case 'batch_set_slot': {
      const cmd: Command = {
        t: 'canvas.batchSetSlot',
        mainId: s('mainId'),
        slotName: s('slotName'),
        value: s('value'),
      };
      const pred = batchPredicateFromInput(input.predicate);
      if (pred) (cmd as { predicate?: BatchPredicate }).predicate = pred;
      return cmd;
    }
    case 'replace_instances':
      return {
        t: 'canvas.replaceInstances',
        fromMainId: s('fromMainId'),
        toMainId: s('toMainId'),
        slotMapping: obj('slotMapping'),
      };

    // Tool frame lifecycle
    case 'create_tool_frame': {
      const tk = s('toolKind');
      const toolKind: 'chat' | 'code' | 'fx' | 'styleguide' =
        tk === 'code' || tk === 'fx' || tk === 'styleguide' ? tk : 'chat';
      const cmd: Command = {
        t: 'toolFrame.create',
        id: s('id'),
        toolKind,
        x: n('x'),
        y: n('y'),
      };
      if (typeof input.anchorId === 'string') (cmd as { anchorId?: string }).anchorId = input.anchorId;
      if (typeof input.pinned === 'boolean') (cmd as { pinned?: boolean }).pinned = input.pinned;
      return cmd;
    }
    case 'set_tool_anchor':
      return {
        t: 'toolFrame.setAnchor',
        id: s('id'),
        anchorId: typeof input.anchorId === 'string' ? input.anchorId : null,
      };

    // Annotations + tours
    case 'create_note':
      return {
        t: 'note.create',
        id: s('id'), x: n('x'), y: n('y'), w: n('w') || 220,
        html: s('html'), title: s('title'),
        target: noteTargetFromInput(input.target),
        tourId: typeof input.tourId === 'string' ? input.tourId : undefined,
        step: typeof input.step === 'number' ? input.step : undefined,
        color: typeof input.color === 'string' ? input.color : undefined,
      };
    case 'set_note': {
      if (typeof input.html === 'string') return { t: 'note.setBody', id: s('id'), html: input.html };
      if ('target' in input) return { t: 'note.setTarget', id: s('id'), target: noteTargetFromInput(input.target) };
      return {
        t: 'note.setMeta', id: s('id'),
        title: typeof input.title === 'string' ? input.title : undefined,
        tourId: typeof input.tourId === 'string' ? input.tourId : undefined,
        step: typeof input.step === 'number' ? input.step : undefined,
        color: typeof input.color === 'string' ? input.color : undefined,
      };
    }
    case 'build_tour':
      return { t: 'tour.upsert', id: s('id'), title: s('title') };
    case 'remove_tour':
      return { t: 'tour.remove', id: s('id') };
    case 'find_tours':
      return null;

    // ---- Styling + behavior ----
    case 'set_frame_theme':
      return {
        t: 'frame.setTheme', id: s('id'),
        themeId: typeof input.themeId === 'string' ? input.themeId : undefined,
      };
    case 'set_frame_script':
      return { t: 'frame.setScript', id: s('id'), script: s('script') };
    case 'create_theme':
      return { t: 'theme.create', theme: input.theme as import('../store/types').ThemeToken };
    case 'update_theme':
      return {
        t: 'theme.update', id: s('id'),
        patch: (input.patch ?? {}) as Partial<import('../store/types').ThemeToken>,
      };
    case 'delete_theme':
      return { t: 'theme.delete', id: s('id') };
    case 'import_design_md':
      return { t: 'theme.importDesignMd', source: s('source') };
    case 'set_active_theme':
      return {
        t: 'doc.setActiveTheme',
        themeId: typeof input.themeId === 'string' ? input.themeId : undefined,
      };

    // TSX frames
    case 'create_tsx_frame':
      return { t: 'tsxFrame.create', id: input.id as string, x: input.x as number, y: input.y as number, w: input.w as number, h: input.h as number | undefined, title: input.title as string, tsx: input.tsx as string };
    case 'set_tsx_source':
      return { t: 'tsx.setSource', id: input.id as string, tsx: input.tsx as string };
    case 'promote_tsx_to_main':
      return { t: 'tsxFrame.promoteToMain', id: input.id as string, locked: input.locked as boolean | undefined };
    case 'create_tsx_instance':
      return { t: 'tsxInstance.create', id: input.id as string, mainId: input.mainId as string, x: input.x as number, y: input.y as number, propValues: (input.propValues as Record<string, never>) ?? {}, w: input.w as number | undefined };
    case 'set_tsx_prop':
      return { t: 'tsxInstance.setProp', id: input.id as string, name: input.name as string, value: input.value as never };
    case 'reset_tsx_prop':
      return { t: 'tsxInstance.resetProp', id: input.id as string, name: input.name as string };

    // Comments — agent feedback loop
    case 'reply_comment':
      return {
        t: 'comment.reply',
        threadId: s('threadId'),
        message: { id: newAgentMsgId(), author: 'agent', text: s('text'), ts: Date.now() },
      };
    case 'resolve_comment':
      return { t: 'comment.resolve', threadId: s('threadId'), resolved: true };
    case 'read_comments':
      return null; // read-only — handled inline by the loop

    // Motion timeline
    case 'motion_init':
      return { t: 'motion.init', frameId: s('frameId') };
    case 'motion_add_track': {
      const cmd: Command = {
        t: 'motion.addTrack',
        frameId: s('frameId'),
        trackId: s('trackId'),
        layerId: s('layerId'),
        target: s('target'),
      };
      if (typeof input.label === 'string') (cmd as { label?: string }).label = input.label;
      return cmd;
    }
    case 'motion_add_layer':
      return {
        t: 'motion.addLayer',
        frameId: s('frameId'),
        trackId: s('trackId'),
        layerId: s('layerId'),
        blend: input.blend === 'replace' ? 'replace' : 'add',
      };
    case 'motion_set_keyframe': {
      const value: number | string = typeof input.value === 'number' ? (input.value as number) : s('value');
      const ease: EaseSpec = (input.ease as EaseSpec) ?? 'linear';
      return {
        t: 'motion.setKeyframe',
        frameId: s('frameId'),
        trackId: s('trackId'),
        layerId: s('layerId'),
        prop: s('prop') as MotionProp,
        kf: { t: n('t'), value, ease },
      };
    }
    case 'motion_move_keyframe':
      return {
        t: 'motion.moveKeyframe',
        frameId: s('frameId'),
        trackId: s('trackId'),
        layerId: s('layerId'),
        prop: s('prop') as MotionProp,
        fromT: n('fromT'),
        toT: n('toT'),
      };
    case 'motion_set_ease':
      return {
        t: 'motion.setEase',
        frameId: s('frameId'),
        trackId: s('trackId'),
        layerId: s('layerId'),
        prop: s('prop') as MotionProp,
        kfT: n('kfT'),
        ease: (input.ease as EaseSpec) ?? 'linear',
      };
    case 'motion_set_duration':
      return { t: 'motion.setDuration', frameId: s('frameId'), duration: n('duration') };
    case 'motion_set_playback':
      return {
        t: 'motion.setPlayback',
        frameId: s('frameId'),
        playback: (input.playback as PlaybackConfig) ?? {},
      };

    // Lottie slot overrides (add_lottie/update_lottie/get_lottie are async — handled
    // in executeToolUse, not mapped to a plain Command here → fall through to null).
    case 'set_lottie_slots': {
      const slots = (input.slots && typeof input.slots === 'object' && !Array.isArray(input.slots)
        ? (input.slots as Record<string, number | number[] | string>)
        : {});
      return { t: 'lottie.setSlots', frameId: s('frameId'), cfNodeId: s('cfNodeId'), slots };
    }

    // Read-only query handled inline by the agent loop (no Command dispatched)
    case 'find_instances':
      return null;

    default:
      return null;
  }
}

function noteTargetFromInput(raw: unknown): import('../store/types').NoteTarget | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const p = raw as Record<string, unknown>;
  if (p.kind === 'frame' && typeof p.frameId === 'string') return { kind: 'frame', frameId: p.frameId };
  if (p.kind === 'pin' && typeof p.x === 'number' && typeof p.y === 'number') return { kind: 'pin', x: p.x, y: p.y };
  if (p.kind === 'element' && typeof p.frameId === 'string' && typeof p.nodeId === 'string') {
    return { kind: 'element', frameId: p.frameId, nodeId: p.nodeId };
  }
  return undefined;
}

function batchPredicateFromInput(raw: unknown): BatchPredicate | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const p = raw as Record<string, unknown>;
  const out: BatchPredicate = {};
  if (typeof p.slotName === 'string') out.slotName = p.slotName;
  if (typeof p.slotEquals === 'string') out.slotEquals = p.slotEquals;
  if (p.inRect && typeof p.inRect === 'object' && !Array.isArray(p.inRect)) {
    const r = p.inRect as Record<string, unknown>;
    if (
      typeof r.x === 'number' && typeof r.y === 'number' &&
      typeof r.w === 'number' && typeof r.h === 'number'
    ) {
      out.inRect = { x: r.x, y: r.y, w: r.w, h: r.h };
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Names of read-only tools the agent loop should answer inline (no dispatch). */
export const READ_ONLY_TOOLS = new Set<string>([
  'find_instances', 'find_tours', 'read_comments',
  'get_canvas_snapshot', 'get_errors',
]);

/** Map agent's loose {kind, intensity?, px?, ...} to a tagged Effect. */
function effectFromInput(input: Record<string, unknown>): Effect {
  const kind = typeof input.kind === 'string' ? (input.kind as string) : 'none';
  const num = (k: string, dflt: number) =>
    typeof input[k] === 'number' ? (input[k] as number) : dflt;
  const str = (k: string, dflt: string) =>
    typeof input[k] === 'string' ? (input[k] as string) : dflt;
  switch (kind) {
    case 'none':
      return { kind: 'none' };
    case 'blur':
      return { kind: 'blur', px: num('px', num('intensity', 0) * 10) };
    case 'grayscale':
      return { kind: 'grayscale', amt: num('amt', num('intensity', 0)) };
    case 'glow':
      return {
        kind: 'glow',
        color: str('color', '#ffffff'),
        radius: num('radius', num('intensity', 0) * 16),
        passes: num('passes', 2),
      };
    case 'outline':
      return { kind: 'outline', color: str('color', '#000000'), width: num('width', 2) };
    case 'crt':
      return { kind: 'crt', intensity: num('intensity', 0.5) };
    case 'chromatic':
      return { kind: 'chromatic', offset: num('offset', num('intensity', 0) * 4) };
    case 'frostedGlass':
      return { kind: 'frostedGlass', blur: num('blur', 8), tint: str('tint', 'rgba(255,255,255,0.1)') };
    default:
      return { kind: 'none' };
  }
}
